import { format } from 'date-fns';
import { isEmpty, omit, omitBy } from 'lodash';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';

import {
  CseBenefitsList,
  CustomSlider,
  DatePicker,
  FeaturedProducts,
  FormSelect,
  HeroBanner,
  InformationsSlider,
  PromoteProducts,
  SearchBar,
  useHomePageContext
} from 'components';
import { PAGE_TYPES } from 'constants';
import { useFilters } from 'hooks';
import { routes } from 'router';

const TravelHomePage = () => {
  const { getHomePage, homePage, homePageLoading, newsCards, featuredProducts } = useHomePageContext();

  useEffect(() => {
    getHomePage();
  }, []);

  return (
    <div className="homepage mb-4" data-test-id="travelHomePage">
      <div className="relative">
        <HeroBanner
          image={homePage?.heroImageDesktop}
          mobileImage={homePage?.heroImageMobile}
          loading={homePageLoading}
          className="hero-container gradient-small"
        />

        <SearchBar>
          <TravelForm />
        </SearchBar>
      </div>

      <div className="main-container pt-12 flex flex-col gap-24">
        <CustomSlider slides={newsCards || []} title={homePage?.newsSliderTitle || ''} loading={homePageLoading} />

        <PromoteProducts />

        {!isEmpty(homePage?.informationSlides) && <InformationsSlider slides={homePage?.informationSlides} />}

        {!isEmpty(featuredProducts) && <FeaturedProducts products={featuredProducts} />}

        <CseBenefitsList title={homePage?.cseBenefitsTitle} />
      </div>
    </div>
  );
};

const TravelForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const form = useForm({
    mode: 'all',
    defaultValues: {
      tourOperatorId: { label: 'Touloisirs', value: 'touloisirs', id: 'touloisirs' },
      country: null,
      departure: null,
      departureDate: [null, null]
    }
  });

  const {
    getMainFilters,
    tourOperatorFilters,
    countryListFilters,
    departureCityFilters,
    loadingTourOperator,
    loadingCountryList,
    loadingDepartureCity
  } = useFilters(['tourOperators', 'countryList', 'departureCityList']);

  const tourOperatorId = form.watch('tourOperatorId');

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getMainFilters();
  }, []);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const onSubmit = () => {
    const { tourOperatorId, departure, departureDate, country } = form.getValues();
    let searchParams = {
      type: PAGE_TYPES.travels,
      tourOperatorId: tourOperatorId.id,
      departure: departure?.id,
      beginDate: departureDate[0] ? format(departureDate[0], 'yyyy-MM-dd') : null,
      endDate: departureDate[1] ? format(departureDate[1], 'yyyy-MM-dd') : null,
      country: country?.id
    };

    // Remove empty values and departureDate key
    searchParams = omitBy(searchParams, isEmpty);
    searchParams = omit(searchParams, 'departureDate');

    navigate({
      pathname: routes.results.path,
      search: createSearchParams(searchParams).toString()
    });
  };

  return (
    <FormProvider {...form}>
      <form className="search-bar" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col grow lg:grid lg:grid-cols-12">
          <div className="filter-container">
            <Controller
              render={({ field: { onChange } }) => (
                <FormSelect
                  onChange={onChange}
                  name="tourOperatorId"
                  options={tourOperatorFilters || []}
                  className="w-full lg:w-fit"
                  label={t('form:provider')}
                  isLoading={loadingTourOperator}
                  id="searchTravelTourOperator"
                />
              )}
              name="tourOperatorId"
              control={form.control}
              rules={{ required: true }}
            />
          </div>

          <div className="filter-container">
            <Controller
              render={({ field: { onChange } }) => (
                <FormSelect
                  onChange={onChange}
                  name="country"
                  options={countryListFilters || []}
                  placeholder={t('form:placeholders.destination')}
                  className="w-full lg:w-fit"
                  label={t('form:destination')}
                  icon={{ name: 'location-dot', style: 'fad' }}
                  isLoading={loadingCountryList}
                  id="searchTravelCountry"
                />
              )}
              name="country"
              control={form.control}
            />
          </div>

          <div className="filter-container">
            <Controller
              render={({ field: { onChange } }) => (
                <FormSelect
                  onChange={onChange}
                  name="departure"
                  options={departureCityFilters || []}
                  placeholder="Paris, Lyon, Marseille, etc ..."
                  className="w-full lg:w-fit"
                  label={t('form:departureCity')}
                  icon={{ name: 'plane-departure', style: 'fad' }}
                  isLoading={loadingDepartureCity}
                  id="searchTravelDeparture"
                />
              )}
              name="departure"
              control={form.control}
            />
          </div>

          <div className="filter-container">
            <Controller
              control={form.control}
              name="departureDate"
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  onChange={onChange}
                  name="departureDate"
                  label={t('form:departureDate')}
                  placeholder={t('form:placeholders.date')}
                  selectsRange
                  startDate={value[0]}
                  endDate={value[1]}
                  containerClass="relative h-full"
                  icon={{ name: 'calendar-days', style: 'fad' }}
                  minDate={new Date()}
                  id="searchTravelDepartureDate"
                />
              )}
            />
          </div>
        </div>

        <button
          type="submit"
          className="btn secondary uppercase mt-2 mx-4 lg:mt-0 lg:mr-2 xl:mr-4"
          disabled={isEmpty(tourOperatorId)}
          data-test-id="searchFormSubmit"
        >
          {t('buttons.search')}
        </button>
      </form>
    </FormProvider>
  );
};

export default TravelHomePage;
